import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tool } from '../types/database';
import { GlassCard } from './ui/GlassCard';
import { ExternalLink, Info, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { createToolSlug } from '../pages/ToolDetailPage';
import { useFavoritesContext } from '../contexts/FavoritesContext';
interface TopToolCardProps {
  tool: Tool;
}
export function TopToolCard({
  tool
}: TopToolCardProps) {
  const {
    toggleFavorite,
    isFavorite
  } = useFavoritesContext();
  const [logoError, setLogoError] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const domain = tool.website_url ? new URL(tool.website_url).hostname : '';
  // Multiple logo sources as fallbacks
  const logoSources = [tool.logo_url, `https://logo.clearbit.com/${domain}`, `https://www.google.com/s2/favicons?domain=${domain}&sz=128`, `https://icons.duckduckgo.com/ip3/${domain}.ico`].filter(Boolean);
  const currentLogoUrl = logoSources[fallbackIndex];
  const handleLogoError = () => {
    if (fallbackIndex < logoSources.length - 1) {
      setFallbackIndex(fallbackIndex + 1);
    } else {
      setLogoError(true);
    }
  };
  // Get category emoji for fallback
  const getCategoryEmoji = (category: string) => {
    const emojiMap: Record<string, string> = {
      Image: '🎨',
      Video: '🎬',
      Audio: '🎵',
      Development: '💻',
      Writing: '✍️',
      Design: '🎨',
      Productivity: '⚡',
      Research: '🔬',
      Marketing: '📱',
      Other: '🔧'
    };
    return emojiMap[category] || '🔧';
  };
  // Get pricing type from tiers
  const getPricingType = () => {
    if (!tool.pricing_tiers || tool.pricing_tiers.length === 0) return 'Contact';
    const hasFree = tool.pricing_tiers.some(t => t.price.toLowerCase().includes('free'));
    const hasPaid = tool.pricing_tiers.some(t => !t.price.toLowerCase().includes('free'));
    if (hasFree && hasPaid) return 'Freemium';
    if (hasFree) return 'Free';
    return 'Paid';
  };
  const isAdmin = (tool as any).source === 'admin';
  // Get categories to display (use new categories array or fallback to single category)
  const displayCategories = tool.categories && tool.categories.length > 0 ? tool.categories : [tool.category];
  // Get category color
  const getCategoryColor = (category: string) => {
    const colorMap: Record<string, string> = {
      Image: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
      Video: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
      Audio: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      Development: 'bg-green-500/10 text-green-400 border-green-500/20',
      Writing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      Marketing: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      Design: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      Productivity: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      Research: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
    };
    return colorMap[category] || 'bg-white/5 text-gray-400 border-white/10';
  };
  return <GlassCard className="h-full flex flex-col group overflow-hidden border-white/5 transition-colors duration-500" data-id="element-672">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-cyan-500/10 group-hover:via-purple-500/10 group-hover:to-pink-500/10 transition-all duration-500" data-id="element-673" />

      <div className="p-5 flex flex-col h-full relative z-10" data-id="element-674">
        <div className="flex justify-between items-start mb-3" data-id="element-675">
          <div className="flex items-start gap-3" data-id="element-676">
            {/* Tool Icon - Clickable, links to detail page */}
            <Link to={`/${createToolSlug(tool.name)}`} className="w-[45px] h-[45px] flex items-center justify-center overflow-hidden rounded-lg shrink-0 bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-colors cursor-pointer" data-id="element-677">
              {!logoError && currentLogoUrl ? <img src={currentLogoUrl} alt={`${tool.name} logo`} className="w-full h-full object-contain" onError={handleLogoError} data-id="element-678" /> : <div className="text-2xl" data-id="element-679">
                  {getCategoryEmoji(displayCategories[0])}
                </div>}
            </Link>

            {/* Source Badge - Only show Curated badge */}
            {isAdmin && <div className="flex flex-col gap-1.5" data-id="element-680">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/20 border border-purple-500/40" data-id="element-681">
                  <Shield size={14} className="text-purple-400 shrink-0" data-id="element-682" />
                  <span className="text-[11px] font-bold text-purple-300 uppercase tracking-wide" data-id="element-683">
                    Curated
                  </span>
                </div>
              </div>}
          </div>

          <div className="flex items-center gap-2" data-id="element-684">
            {/* Favorite Icon */}
            <button onClick={() => toggleFavorite(tool.id)} className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all cursor-pointer" data-id="element-685">
              <motion.div animate={{
              scale: isFavorite(tool.id) ? [1, 1.2, 1] : 1
            }} transition={{
              duration: 0.3
            }} data-id="element-686">
                <svg width="14" height="14" viewBox="0 0 24 24" fill={isFavorite(tool.id) ? '#ef4444' : 'none'} stroke={isFavorite(tool.id) ? '#ef4444' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-colors ${isFavorite(tool.id) ? 'text-red-500' : 'text-gray-400'}`} data-id="element-687">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" data-id="element-688" />
                </svg>
              </motion.div>
            </button>

            {/* Category Badges - Show first category, or +N if multiple */}
            <div className="flex items-center gap-1" data-id="element-689">
              <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getCategoryColor(displayCategories[0])}`} data-id="element-690">
                {displayCategories[0]}
              </span>
              {displayCategories.length > 1 && <span className="px-2 py-1 rounded-md text-xs font-medium border bg-white/5 text-gray-400 border-white/10" data-id="element-691">
                  +{displayCategories.length - 1}
                </span>}
            </div>
          </div>
        </div>

        <div className="mb-2" data-id="element-692">
          <Link to={`/${createToolSlug(tool.name)}`} className="block" data-id="element-693">
            <h3 className="text-base text-white hover:text-cyan-400 transition-colors duration-300 font-special uppercase tracking-wide cursor-pointer" style={{
            marginBottom: 0
          }} data-id="element-694">
              {tool.name}
            </h3>
          </Link>
        </div>

        {/* Description */}
        <p className="text-gray-400 text-sm leading-snug mb-[30px] group-hover:text-gray-300 transition-colors font-sans flex-grow" data-id="element-695">
          {tool.description}
        </p>

        {/* Mini Pricing Table */}
        {tool.pricing_tiers && tool.pricing_tiers.length > 0 && <div className="mb-3 bg-white/5 rounded-lg border border-white/5 overflow-hidden" data-id="element-696">
            <div className="divide-y divide-white/5" data-id="element-697">
              {tool.pricing_tiers.slice(0, 3).map((tier, index) => <div key={index} className="flex justify-between items-center px-3 py-1.5 hover:bg-white/5 transition-colors" data-id="element-698">
                  <span className="text-xs text-gray-400 font-medium font-sans" data-id="element-699">
                    {tier.name}
                  </span>
                  <span className="text-xs text-green-400 font-bold font-sans" data-id="element-700">
                    {tier.price}
                  </span>
                </div>)}
            </div>
          </div>}

        {/* Bottom action */}
        <div className="pt-3 border-t border-white/5 flex items-center justify-between gap-2" data-id="element-701">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-400 font-sans" data-id="element-702">
            {getPricingType()}
          </span>

          <div className="flex items-center gap-2" data-id="element-703">
            <Link to={`/${createToolSlug(tool.name)}`} className="flex items-center gap-2 text-sm text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all" style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 400
          }} data-id="element-704">
              <Info size={14} data-id="element-705" />
              <span data-id="element-706">Info</span>
            </Link>

            <motion.a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all cursor-pointer" style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 400
          }} whileTap={{
            scale: 0.95
          }} data-id="element-707">
              Website <ExternalLink size={14} data-id="element-708" />
            </motion.a>
          </div>
        </div>
      </div>
    </GlassCard>;
}